<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store';

import { useAccount } from 'dashboard/composables/useAccount';

import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import ChannelCatalogCard from 'dashboard/components/widgets/ChannelCatalogCard.vue';
import { useChannelConnectDefinitions } from 'dashboard/composables/useChannelConnectDefinitions';

import {
  CATEGORY_SECTION_ORDER,
  CHANNEL_KEY_TO_CATEGORY,
  isChannelCatalogComingSoon,
  isChannelCatalogItemActive,
} from 'shared/helpers/channelCatalog';

const { t } = useI18n();

function sectionHeadingLabel(sectionId) {
  switch (sectionId) {
    case 'business_messaging':
      return t('INBOX_MGMT.CHANNEL_CATALOG.SECTIONS.BUSINESS_MESSAGING');
    case 'calls':
      return t('INBOX_MGMT.CHANNEL_CATALOG.SECTIONS.CALLS');
    case 'sms':
      return t('INBOX_MGMT.CHANNEL_CATALOG.SECTIONS.SMS');
    case 'email':
      return t('INBOX_MGMT.CHANNEL_CATALOG.SECTIONS.EMAIL');
    case 'live_chat':
      return t('INBOX_MGMT.CHANNEL_CATALOG.SECTIONS.LIVE_CHAT');
    default:
      return '';
  }
}
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const globalConfig = useMapGetter('globalConfig/get');

const enabledFeatures = ref({});

const searchQuery = ref('');
const activeCatalogTabIndex = ref(0);

const oauthEnv = computed(() => ({
  fbAppId: window.chatwootConfig?.fbAppId,
  instagramAppId: window.chatwootConfig?.instagramAppId,
  tiktokAppId: window.chatwootConfig?.tiktokAppId,
}));

/** Default catalog pill badges (installation config can still override per key). */
const CATALOG_BADGE_META = {
  tiktok: { tone: 'info' },
  facebook: { icon: 'i-ri-medal-line', tone: 'success' },
  whatsapp: { icon: 'i-ri-medal-line', tone: 'success' },
  whatsapp_browser: { icon: 'i-ri-hand-coin-line', tone: 'violet' },
};

const catalogTabs = computed(() => [
  { id: 'all', label: t('INBOX_MGMT.CHANNEL_CATALOG.TABS.ALL') },
  {
    id: 'business_messaging',
    label: t('INBOX_MGMT.CHANNEL_CATALOG.TABS.BUSINESS_MESSAGING'),
  },
  { id: 'calls', label: t('INBOX_MGMT.CHANNEL_CATALOG.TABS.CALLS') },
  { id: 'sms', label: t('INBOX_MGMT.CHANNEL_CATALOG.TABS.SMS') },
  { id: 'email', label: t('INBOX_MGMT.CHANNEL_CATALOG.TABS.EMAIL') },
  { id: 'live_chat', label: t('INBOX_MGMT.CHANNEL_CATALOG.TABS.LIVE_CHAT') },
]);

const channelDefinitions = useChannelConnectDefinitions();

const badgesByKey = computed(
  () => globalConfig.value.channelCatalogBadges || {}
);

const channelsWithMeta = computed(() =>
  channelDefinitions.value.map(ch => {
    const isActive = isChannelCatalogItemActive(
      ch.key,
      enabledFeatures.value,
      oauthEnv.value
    );
    const globalBadge = badgesByKey.value[ch.key];
    const hasGlobalContent =
      globalBadge &&
      (String(globalBadge.text || '').trim() ||
        String(globalBadge.emoji || '').trim() ||
        String(globalBadge.icon || '').trim());
    const staticDef = CATALOG_BADGE_META[ch.key];
    let badge = null;
    if (hasGlobalContent) {
      badge = globalBadge;
    } else if (staticDef) {
      let text = '';
      switch (ch.key) {
        case 'tiktok':
          text = t('INBOX_MGMT.CHANNEL_CATALOG.BADGES.BETA');
          break;
        case 'facebook':
        case 'whatsapp':
          text = t('INBOX_MGMT.CHANNEL_CATALOG.BADGES.POPULAR');
          break;
        case 'whatsapp_browser':
          text = t('INBOX_MGMT.CHANNEL_CATALOG.BADGES.NO_CHARGE_BY_MESSAGE');
          break;
        default:
          text = '';
      }
      if (text) {
        badge = {
          text,
          tone: staticDef.tone,
        };
        if (staticDef.icon) {
          badge.icon = staticDef.icon;
        }
      }
    }
    return {
      ...ch,
      categoryId: CHANNEL_KEY_TO_CATEGORY[ch.key] || 'business_messaging',
      badge,
      isActive,
      isComingSoon: isChannelCatalogComingSoon(ch.key, isActive),
    };
  })
);

const activeCategoryId = computed(
  () => catalogTabs.value[activeCatalogTabIndex.value]?.id || 'all'
);

/** Section heading when a single category tab is selected (same copy as grouped "All" view). */
const activeFilterSectionTitle = computed(() => {
  const id = activeCategoryId.value;
  if (id === 'all') return '';
  return sectionHeadingLabel(id);
});

const searchFilteredChannels = computed(() => {
  const q = searchQuery.value.trim();
  const base = channelsWithMeta.value;
  if (!q) return base;
  return picoSearch(base, q, ['title', 'description']);
});

const tabFilteredChannels = computed(() => {
  const cat = activeCategoryId.value;
  const list = searchFilteredChannels.value;
  if (cat === 'all') return list;
  return list.filter(ch => ch.categoryId === cat);
});

const groupedSections = computed(() => {
  if (activeCategoryId.value !== 'all') return [];

  const list = searchFilteredChannels.value;
  const byCat = {};
  CATEGORY_SECTION_ORDER.forEach(id => {
    byCat[id] = [];
  });
  list.forEach(ch => {
    const cid = ch.categoryId;
    if (byCat[cid]) byCat[cid].push(ch);
  });

  return CATEGORY_SECTION_ORDER.filter(id => byCat[id].length > 0).map(id => ({
    id,
    title: sectionHeadingLabel(id),
    channels: byCat[id],
  }));
});

const initializeEnabledFeatures = async () => {
  enabledFeatures.value = currentAccount.value.features;
};

const initChannelAuth = channelKey => {
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { sub_page: channelKey, accountId: accountId.value },
  });
};

const metaMessageTemplatesLearnMoreHref = channelKey =>
  ['facebook', 'instagram'].includes(channelKey)
    ? '/messenger-message-template'
    : null;

const onCatalogTabChange = tab => {
  const idx = catalogTabs.value.findIndex(x => x.id === tab.id);
  activeCatalogTabIndex.value = idx >= 0 ? idx : 0;
};

onMounted(() => {
  initializeEnabledFeatures();
});
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4 pb-6">
    <BaseSettingsHeader
      v-model:search-query="searchQuery"
      :title="$t('INBOX_MGMT.CHANNEL_CATALOG.TITLE')"
      :description="$t('INBOX_MGMT.CHANNEL_CATALOG.DESCRIPTION')"
      :search-placeholder="$t('INBOX_MGMT.CHANNEL_CATALOG.SEARCH_PLACEHOLDER')"
      show-back-button
      :back-url="{
        name: 'settings_inbox_list',
        params: { accountId: accountId },
      }"
    >
      <template #tabs>
        <div class="min-w-0 max-w-full overflow-x-auto pb-0.5">
          <TabBar
            :initial-active-tab="activeCatalogTabIndex"
            :tabs="catalogTabs"
            @tab-changed="onCatalogTabChange"
          />
        </div>
      </template>
    </BaseSettingsHeader>
    <div class="min-h-0 flex-1 ps-4 pe-8 md:ps-5 md:pe-10">
      <p
        v-if="searchQuery.trim() && !searchFilteredChannels.length"
        class="py-16 text-center text-body-main text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.CHANNEL_CATALOG.NO_RESULTS') }}
      </p>
      <template v-else-if="activeCategoryId === 'all'">
        <div
          v-for="section in groupedSections"
          :key="section.id"
          class="flex flex-col gap-4"
        >
          <h2
            class="mb-0 pb-3 pt-2 text-heading-3 font-semibold text-n-slate-12 md:pb-4 md:pt-3"
          >
            {{ section.title }}
          </h2>
          <div
            class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
          >
            <ChannelCatalogCard
              v-for="ch in section.channels"
              :key="ch.key"
              :catalog-key="ch.key"
              :title="ch.title"
              :description="ch.description"
              :svg-slug="ch.svgSlug"
              :icon="ch.icon"
              :badge="ch.badge"
              :is-coming-soon="ch.isComingSoon"
              :disabled="!ch.isActive"
              :learn-more-href="metaMessageTemplatesLearnMoreHref(ch.key)"
              @connect="initChannelAuth(ch.key)"
            />
          </div>
        </div>
      </template>

      <div v-else class="flex flex-col gap-4">
        <h2
          class="mb-0 pb-3 pt-2 text-heading-3 font-semibold text-n-slate-12 md:pb-4 md:pt-3"
        >
          {{ activeFilterSectionTitle }}
        </h2>
        <div
          class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          <ChannelCatalogCard
            v-for="ch in tabFilteredChannels"
            :key="ch.key"
            :catalog-key="ch.key"
            :title="ch.title"
            :description="ch.description"
            :svg-slug="ch.svgSlug"
            :icon="ch.icon"
            :badge="ch.badge"
            :is-coming-soon="ch.isComingSoon"
            :disabled="!ch.isActive"
            :learn-more-href="metaMessageTemplatesLearnMoreHref(ch.key)"
            @connect="initChannelAuth(ch.key)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
